import { csrf_headers, get_token } from '@/router/index.js';
import {
  build_group_member_ids,
  build_silent_poll_update,
  create_floating_chat_state,
} from './floating_chat_widget_helpers.js';
import { floating_chat_action_methods } from './floating_chat_widget_action_methods.js';
import { floating_chat_display_methods } from './floating_chat_widget_display_methods.js';
import { floating_chat_send_methods } from './floating_chat_widget_send_methods.js';

const API_BASE = process.env.VUE_APP_API_BASE_URL || '';

export default {
  name: 'FloatingChatWidget',

  data: create_floating_chat_state,

  computed: {
    is_available() {
      return this.is_available_override || Boolean(get_token());
    },

    active_conversation() {
      return this.conversations.find(conversation => String(conversation.id) === String(this.active_conversation_id)) || null;
    },

    active_is_group() {
      return this.is_group_conversation(this.active_conversation);
    },

    active_conversation_members() {
      return this.get_conversation_members(this.active_conversation);
    },

    active_group_can_manage() {
      if (!this.active_is_group) return false;
      const me = this.active_conversation_members.find(member => member.is_me);
      const role = String(me?.conversation_role || me?.group_role || '').toUpperCase();
      const created_by = this.active_conversation?.created_by;
      return role === 'ADMIN' || role === 'OWNER' || String(created_by) === String(this.current_user_id);
    },

    total_unread() {
      return this.conversations.reduce((total, conversation) => total + Number(conversation.unread_count || 0), 0);
    },

    filtered_conversations() {
      const query = this.search_query.trim().toLowerCase();

      return this.conversations
        .filter(conversation => this.is_visible_conversation(conversation))
        .filter(conversation => {
          if (this.filter === 'direct') return !this.is_group_conversation(conversation);
          if (this.filter === 'spaces') return this.is_group_conversation(conversation);
          return true;
        })
        .filter(conversation => this.conversation_matches_query(conversation, query))
        .sort((a, b) => Number(Boolean(b.is_pinned)) - Number(Boolean(a.is_pinned))
          || new Date(b.updated_at || b.last_message?.created_at || 0) - new Date(a.updated_at || a.last_message?.created_at || 0));
    },

    message_search_results() {
      const query = this.global_search_query.trim().toLowerCase();
      if (!query) return [];
      return this.messages.filter(message => this.message_matches_query(message, query));
    },

    direct_count() {
      return this.conversations.filter(conversation => this.is_visible_conversation(conversation) && !this.is_group_conversation(conversation)).length;
    },

    space_count() {
      return this.conversations.filter(conversation => this.is_visible_conversation(conversation) && this.is_group_conversation(conversation)).length;
    },

    panel_style() {
      const translate = this.mobile_drag_delta > 0
        ? `translateY(${Math.min(this.mobile_drag_delta, 140)}px)`
        : undefined;

      return ({
        position: 'relative',
        width: 'min(532px, calc(100vw - 32px))',
        height: 'min(780px, calc(100vh - 104px))',
        margin: '0 0 16px 0',
        pointerEvents: 'auto',
        overflow: 'hidden',
        transform: translate,
      });
    },

    filtered_users() {
      const query = this.user_search.trim().toLowerCase();
      return this.available_users
        .filter(user => {
          if (!query) return true;
          return [
            this.get_user_name(user),
            user.email,
            user.username,
            user.role,
            user.organization,
            user.platform,
          ].filter(Boolean).join(' ').toLowerCase().includes(query);
        })
        .sort((a, b) => {
          const a_is_me = String(a.id) === String(this.current_user_id);
          const b_is_me = String(b.id) === String(this.current_user_id);
          if (a_is_me !== b_is_me) return a_is_me ? -1 : 1;
          return this.get_user_name(a).localeCompare(this.get_user_name(b));
        });
    },

    selected_group_users() {
      const selected = new Set(this.new_group_user_ids.map(id => String(id)));
      return this.available_users.filter(user => selected.has(String(user.id)));
    },

    group_modal_member_count() {
      if (this.new_conversation_mode !== 'group') return this.new_group_user_ids.length;
      return new Set([
        ...this.new_group_user_ids.map(id => String(id)),
        String(this.current_user_id),
      ].filter(Boolean)).size;
    },
  },

  watch: {
    is_open(open) {
      if (open) this.load_initial_data();
    },
  },

  mounted() {
    document.addEventListener('click', this.close_outside_menus);
    window.addEventListener('keydown', this.handle_keydown);
  },

  beforeUnmount() {
    this.stop_polling();
    this.revoke_preview_url();
    document.removeEventListener('click', this.close_outside_menus);
    window.removeEventListener('keydown', this.handle_keydown);
  },

  methods: {
    require_token() {
      const token = get_token();
      if (!token) this.show_toast('Sign in required', 'Please sign in to use AIC One Chat.', 'error');
      return token;
    },

    auth_headers(content_type = 'application/json') {
      const token = get_token();
      const headers = {
        'X-Page-URL': window.location.href,
        ...csrf_headers(),
      };
      if (token) headers.Authorization = `Token ${token}`;
      if (content_type) headers['Content-Type'] = content_type;
      return headers;
    },

    async request(path, options = {}) {
      const response = await fetch(`${API_BASE}${path}`, {
        credentials: 'include',
        ...options,
        headers: {
          ...this.auth_headers(options.content_type),
          ...(options.headers || {}),
        },
      });
      const body = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(body.message || `Request failed: HTTP ${response.status}`);
      return body;
    },

    toggle_open() {
      this.is_open = !this.is_open;
      if (this.is_open) this.close_drawers();
    },

    collapse_chat() {
      this.is_open = false;
      this.close_drawers();
    },

    async load_initial_data() {
      if (!this.require_token()) return;
      if (this.conversations_loading && this.conversations_loaded) return;

      await this.load_current_user();
      await Promise.all([
        this.load_conversations(),
        this.load_available_users(),
      ]);
      this.start_polling();
      this.sync_ic_workbook_portal_conversations();
    },

    async load_current_user() {
      await this.request('/aic_one_api/system_management/get_current_user/', {
        method: 'GET',
        content_type: null,
      })
        .then(body => this.apply_current_user_response(body))
        .catch(error => this.show_toast('Unable to load user', error.message || 'Please try again.', 'error'));
    },

    apply_current_user_response(body) {
      if (body?.status !== 'success') return;
      this.current_user = body.data;
      this.current_user_id = body.data?.id;
    },

    async load_available_users() {
      this.users_loading = true;
      try {
        const body = await this.request('/aic_one_api/aic_drive/get_available_users/', {
          method: 'GET',
          content_type: null,
        });
        this.available_users = body?.status === 'success' ? body.data || [] : [];
      } catch {
        this.available_users = [];
      } finally {
        this.users_loading = false;
      }
    },

    async load_conversations(silent = false) {
      if (!silent) this.conversations_loading = true;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/get_user_conversations/', {
          method: 'GET',
          content_type: null,
        });
        if (body?.status === 'success') {
          this.conversations = (body.data || [])
            .filter(summary => !this.hidden_conversation_ids.includes(summary.id) || summary.last_message)
            .map(summary => {
              const existing = this.conversations.find(c => String(c.id) === String(summary.id));
              if (!existing?.participants?.length || summary.participants?.length) return summary;
              return ({
                ...summary,
                participants: existing.participants,
                member_count: summary.member_count || existing.member_count,
              });
            });
        }
      } catch (error) {
        if (!silent) this.show_toast('Unable to load chats', error.message || 'Please try again.', 'error');
      } finally {
        this.conversations_loading = false;
        this.conversations_loaded = true;
      }
    },

    async load_conversation_detail(conversation_id) {
      const params = new URLSearchParams({ conversation_id });

      const body = await this.request(`/aic_one_api/one_chat_management/get_conversation_detail/?${params}`, {
        method: 'GET',
        content_type: null,
      }).catch(() => null);

      if (body?.status === 'success' && body.data?.id) {
        this.upsert_conversation(body.data);
      }
    },

    async sync_ic_workbook_portal_conversations() {
      if (this._portal_sync_in_flight) return;
      this._portal_sync_in_flight = true;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/sync_ic_workbook_portal_conversations/', {
          method: 'POST',
          body: JSON.stringify({ source: 'ic_workbook' }),
        });
        if (body?.status === 'success' && Array.isArray(body.data)) {
          body.data.forEach(conversation => this.upsert_conversation(conversation));
        }
      } catch {
        // Keep the floating widget quiet if portal sync is unavailable.
      } finally {
        this._portal_sync_in_flight = false;
      }
    },

    start_polling() {
      this.stop_polling();
      this._polling_active = true;
      const poll = async () => {
        await this.run_polling_cycle().catch(error => console.error('Floating chat polling failed:', error));
        if (this._polling_active) this.poll_timer = window.setTimeout(poll, this.poll_frequency);
      };
      poll();
    },

    async run_polling_cycle() {
      if (this.active_conversation_id) {
        await this.load_messages(this.active_conversation_id, true);
        await this.load_pinned_messages(this.active_conversation_id);
      }
      await this.load_conversations(true);
      await this.sync_ic_workbook_portal_conversations();
    },

    stop_polling() {
      this._polling_active = false;
      if (this.poll_timer) window.clearTimeout(this.poll_timer);
      this.poll_timer = null;
    },

    select_filter(filter) {
      this.filter = filter;
      if (filter === 'direct') this.active_section = 'direct';
      if (filter === 'spaces') this.active_section = 'spaces';
      if (filter === 'all') this.active_section = 'recent';
      this.active_conversation_id = null;
      this.messages = [];
      this.pinned_messages = [];
    },

    select_section(section) {
      this.active_section = section;
      if (section === 'direct') this.filter = 'direct';
      else if (section === 'spaces') this.filter = 'spaces';
      else this.filter = 'all';
    },

    async select_conversation(conversation) {
      const conversation_id = conversation?.id || conversation;
      this.active_conversation_id = conversation_id;
      this.messages = [];
      this.messages_offset = 0;
      this.messages_has_more = false;
      this.messages_total = 0;
      this.user_scrolled_up = false;
      this.new_messages_banner = false;
      this.reply_to = null;
      this.cancel_edit();
      this.close_drawers();

      await Promise.all([
        this.load_conversation_detail(conversation_id),
        this.load_messages(conversation_id),
        this.load_pinned_messages(conversation_id),
      ]);
    },

    async load_messages(conversation_id, silent = false, load_more = false) {
      if (this.should_skip_message_load(silent)) return;
      await this.with_messages_loading(silent, load_more, () => this.fetch_and_apply_messages(conversation_id, silent, load_more));
    },

    should_skip_message_load(silent) {
      return silent && !this._ready_for_poll;
    },

    async with_messages_loading(silent, load_more, callback) {
      this.set_messages_loading_state(silent, load_more, true);
      try {
        await callback();
      } catch (error) {
        if (!silent) this.show_toast('Unable to load messages', error.message || 'Please try again.', 'error');
      } finally {
        this.set_messages_loading_state(silent, load_more, false);
      }
    },

    async fetch_and_apply_messages(conversation_id, silent, load_more) {
      const body = await this.request('/aic_one_api/one_chat_management/get_conversation_messages/', {
        method: 'POST',
        body: JSON.stringify({
          conversation_id,
          limit: 50,
          offset: load_more ? this.messages_offset : 0,
        }),
      });
      if (body?.status !== 'success') return;

      const { messages: fetched = [], has_more, total } = body.data || {};
      this.apply_loaded_messages({ conversation_id, fetched, has_more, total, silent, load_more });
    },

    set_messages_loading_state(silent, load_more, loading) {
      if (!silent && !load_more) this.messages_loading = loading;
      if (load_more) this.loading_more_messages = loading;
    },

    apply_loaded_messages({ conversation_id, fetched, has_more, total, silent, load_more }) {
      if (load_more) {
        this.handle_load_more(fetched, has_more, total);
        return;
      }
      if (silent) {
        this.apply_silent_poll(fetched, conversation_id, has_more, total);
        return;
      }
      this.replace_active_messages(fetched, has_more, total, conversation_id);
    },

    replace_active_messages(fetched, has_more, total, conversation_id) {
      this.messages = fetched;
      this.messages_offset = fetched.length;
      this.messages_has_more = has_more;
      this.messages_total = total;
      this._messages_initialized = true;
      this._ready_for_poll = true;
      this.scroll_to_bottom(true);
      this.mark_as_read(conversation_id);
    },

    load_more_messages() {
      if (this.loading_more_messages || !this.messages_has_more) return;
      this.load_messages(this.active_conversation_id, false, true);
    },

    handle_load_more(fetched, has_more, total) {
      this.messages_has_more = has_more;
      this.messages_total = total;
      if (!fetched.length) return;

      const el = this.$refs.message_scroller;
      let anchor_el = null;
      let anchor_offset = 0;

      if (el) {
        const container_rect = el.getBoundingClientRect();
        for (const row of el.querySelectorAll('.of-message-row')) {
          if (row.getBoundingClientRect().bottom > container_rect.top) {
            anchor_el = row;
            anchor_offset = row.getBoundingClientRect().top - container_rect.top;
            break;
          }
        }
      }

      const existing_ids = new Set(this.messages.map(message => message.id));
      const unique_older = fetched.filter(message => !existing_ids.has(message.id));
      if (!unique_older.length) return;

      this.messages = [...unique_older, ...this.messages];
      this.messages_offset += unique_older.length;

      this.$nextTick(() => {
        if (!el || !anchor_el) return;
        const container_rect = el.getBoundingClientRect();
        const correction = anchor_el.getBoundingClientRect().top - container_rect.top - anchor_offset;
        this._is_adjusting_scroll = true;
        el.scrollTop += correction;
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this._is_adjusting_scroll = false;
          });
        });
      });
    },

    apply_silent_poll(latest_50, conversation_id, has_more, total) {
      this.messages_has_more = has_more;
      this.messages_total = total;
      if (!latest_50.length) return;

      const update = build_silent_poll_update(this.messages, latest_50);
      if (!update.has_changes) return;

      const el = this.$refs.message_scroller;
      const was_at_bottom = !this.user_scrolled_up;
      const prev_scroll_top = el ? el.scrollTop : 0;
      const prev_scroll_height = el ? el.scrollHeight : 0;

      this.messages = update.brand_new.length ? [...update.patched, ...update.brand_new] : update.patched;
      if (update.brand_new.length) this.messages_offset += update.brand_new.length;

      this.$nextTick(() => {
        this.sync_silent_poll_scroll({
          el,
          update,
          conversation_id,
          was_at_bottom,
          prev_scroll_top,
          prev_scroll_height,
        });
      });
    },

    sync_silent_poll_scroll({ el, update, conversation_id, was_at_bottom, prev_scroll_top, prev_scroll_height }) {
      if (!el) return;
      if (!update.brand_new.length) {
        this.set_scroll_top(el, prev_scroll_top);
        return;
      }
      if (was_at_bottom) {
        el.scrollTop = el.scrollHeight;
        if (update.brand_new.some(message => !this.is_my_message(message))) this.mark_as_read(conversation_id);
        return;
      }
      this.set_scroll_top(el, prev_scroll_top + el.scrollHeight - prev_scroll_height);
      this.new_messages_banner = Math.max(...update.brand_new.map(message => message.id || 0)) > update.current_max_id;
    },

    async mark_as_read(conversation_id) {
      const last_message = this.messages[this.messages.length - 1];
      if (!last_message || !this.messages.some(message => !this.is_my_message(message))) return;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/mark_messages_as_read/', {
          method: 'POST',
          body: JSON.stringify({ conversation_id, last_message_id: last_message.id }),
        });
        if (body?.status === 'success') {
          const conv = this.conversations.find(item => String(item.id) === String(conversation_id));
          if (conv) conv.unread_count = 0;
          window.dispatchEvent(new Event('refresh-unread-chats'));
        }
      } catch {
        // Non-blocking.
      }
    },

    async mark_conversation_read(conversation) {
      if (!conversation) return;
      this.active_context_menu = null;
      if (String(conversation.id) === String(this.active_conversation_id)) {
        await this.mark_as_read(conversation.id);
        return;
      }
      const last_message_id = conversation.last_message?.id;
      if (!last_message_id) return;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/mark_messages_as_read/', {
          method: 'POST',
          body: JSON.stringify({ conversation_id: conversation.id, last_message_id }),
        });
        if (body?.status === 'success') conversation.unread_count = 0;
      } catch {
        // Non-blocking.
      }
    },

    async send_message() {
      const content = this.draft.trim();
      this.draft_error = '';
      if (!content && !this.selected_file) {
        this.draft_error = 'Type a message or attach a file before sending.';
        return;
      }
      if (!this.active_conversation || this.sending) return;

      const backup = this.prepare_message_send();
      this.sending = true;

      try {
        const body = await this.post_message_form(content, backup);
        await this.apply_sent_message(body.data, backup.had_file);
      } catch (error) {
        this.restore_message_send(backup);
        this.show_toast('Message not sent', error.message || 'Please try again.', 'error');
      } finally {
        this.sending = false;
        this.$nextTick(() => {
          if (this.$refs.file_input) this.$refs.file_input.value = '';
        });
      }
    },

    handle_file_select(event) {
      const file = event.target.files?.[0];
      if (!file) return;
      this.selected_file = file;
      this.revoke_preview_url();
      if (this.is_image_file(file.name)) this.selected_file_preview_url = URL.createObjectURL(file);
    },

    remove_selected_file() {
      this.selected_file = null;
      this.revoke_preview_url();
      if (this.$refs.file_input) this.$refs.file_input.value = '';
    },

    revoke_preview_url() {
      if (this.selected_file_preview_url) {
        URL.revokeObjectURL(this.selected_file_preview_url);
        this.selected_file_preview_url = null;
      }
    },

    open_attachment() {
      this.$refs.file_input?.click();
    },

    open_conversation_details() {
      this.conversation_details_open = true;
    },

    async start_conversation(user) {
      this.new_conversation_user_id = user?.id || '';
      await this.start_new_conversation();
    },

    async start_new_conversation() {
      if (!this.new_conversation_user_id || this.starting_conversation) return;
      this.starting_conversation = true;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/start_direct_conversation/', this.get_direct_conversation_request());
        if (body?.status !== 'success') throw new Error(body?.message || 'Failed to start conversation');
        await this.handle_direct_conversation_started(body.data?.conversation || body.data);
      } catch (error) {
        this.show_toast('Conversation not started', error.message || 'Please try again.', 'error');
      } finally {
        this.starting_conversation = false;
      }
    },

    get_direct_conversation_request() {
      return ({
        method: 'POST',
        body: JSON.stringify({
          recipient_id: parseInt(this.new_conversation_user_id, 10),
          initial_message: this.new_conversation_message || '',
        }),
      });
    },

    async handle_direct_conversation_started(conversation) {
      this.upsert_conversation(this.hydrate_direct_conversation(conversation));
      this.close_new_chat_modal();
      await this.select_conversation(conversation.id);
      this.show_toast('Conversation started', this.get_conversation_name(conversation), 'success');
    },

    async create_space() {
      await this.start_new_group_conversation();
    },

    async start_new_group_conversation() {
      const group_name = this.new_group_name.trim();
      if (!this.can_start_group_conversation(group_name)) return;
      this.starting_group = true;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/start_group_conversation/', this.get_group_conversation_request(group_name));
        if (body?.status !== 'success') throw new Error(body?.message || 'Failed to create group');
        await this.handle_group_conversation_started(body.data?.conversation || body.data, group_name);
      } catch (error) {
        this.show_toast('Group not created', error.message || 'Please try again.', 'error');
      } finally {
        this.starting_group = false;
      }
    },

    can_start_group_conversation(group_name) {
      return Boolean(group_name && this.new_group_user_ids.length && !this.starting_group);
    },

    get_group_conversation_request(group_name) {
      const member_ids = build_group_member_ids(this.new_group_user_ids, this.current_user_id);
      return ({
        method: 'POST',
        body: JSON.stringify({
          name: group_name,
          group_name,
          participant_ids: member_ids,
          member_ids,
          initial_message: this.new_conversation_message || '',
        }),
      });
    },

    async handle_group_conversation_started(conversation, group_name) {
      this.upsert_conversation({ ...conversation, name: conversation.name || group_name, is_group: true });
      this.close_new_chat_modal();
      await this.select_conversation(conversation.id);
      this.show_toast('Group created', group_name, 'success');
    },

    hydrate_direct_conversation(conversation) {
      if (!conversation || conversation.participants?.length) return conversation;
      const other = this.available_users.find(user => String(user.id) === String(this.new_conversation_user_id));
      if (!other) return conversation;
      return ({
        ...conversation,
        participants: [
          { id: this.current_user_id, ...this.current_user },
          other,
        ],
      });
    },

    close_new_chat_modal() {
      this.new_chat_open = false;
      this.new_conversation_mode = 'direct';
      this.new_conversation_user_id = '';
      this.new_group_name = '';
      this.new_group_user_ids = [];
      this.new_conversation_message = '';
      this.user_search = '';
    },

    select_modal_user(user) {
      if (this.new_conversation_mode === 'group' && String(user?.id) === String(this.current_user_id)) return;
      if (this.new_conversation_mode === 'group') {
        this.toggle_group_user(user.id);
        return;
      }
      this.new_conversation_user_id = user.id;
    },

    is_group_user_selected(user_id) {
      if (this.new_conversation_mode === 'group' && String(user_id) === String(this.current_user_id)) return true;
      return this.new_group_user_ids.map(id => String(id)).includes(String(user_id));
    },

    toggle_group_user(user_id) {
      if (this.is_group_user_selected(user_id)) {
        this.new_group_user_ids = this.new_group_user_ids.filter(id => String(id) !== String(user_id));
      } else {
        this.new_group_user_ids = [...this.new_group_user_ids, user_id];
      }
    },

    add_member_to_space(user) {
      if (!this.active_is_group || !user?.id) {
        this.toggle_group_user(user.id);
        return;
      }
      this.add_group_member(user.id);
    },

    async add_group_member(user_id = this.group_member_user_id) {
      if (!user_id || this.group_members_updating) return;
      this.group_members_updating = true;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/add_group_members/', {
          method: 'POST',
          body: JSON.stringify({
            conversation_id: this.active_conversation_id,
            member_ids: [parseInt(user_id, 10)],
          }),
        });
        if (body?.status !== 'success') throw new Error(body?.message || 'Failed to add member');
        this.upsert_conversation(body.data);
        this.add_member_open = false;
        this.show_toast('Member added', 'The group has been updated.', 'success');
      } catch (error) {
        this.show_toast('Add member failed', error.message || 'Please try again.', 'error');
      } finally {
        this.group_members_updating = false;
      }
    },

    confirm_remove_member(user) {
      this.remove_member_target = user;
    },

    async remove_member() {
      const member = this.remove_member_target;
      if (!member?.id || this.group_members_updating) return;
      if (!this.active_conversation_id) {
        this.new_group_user_ids = this.new_group_user_ids.filter(id => String(id) !== String(member.id));
        this.remove_member_target = null;
        return;
      }
      this.group_members_updating = true;
      try {
        const body = await this.request('/aic_one_api/one_chat_management/remove_group_member/', {
          method: 'POST',
          body: JSON.stringify({
            conversation_id: this.active_conversation_id,
            user_id: member.id,
          }),
        });
        if (body?.status !== 'success') throw new Error(body?.message || 'Failed to remove member');
        this.upsert_conversation(body.data);
        this.remove_member_target = null;
        this.show_toast('Member removed', 'The group has been updated.', 'success');
      } catch (error) {
        this.show_toast('Remove member failed', error.message || 'Please try again.', 'error');
      } finally {
        this.group_members_updating = false;
      }
    },

    open_search_overlay() {
      this.global_search_query = '';
      this.search_overlay_open = true;
      this.$nextTick(() => this.$refs.global_search_input?.focus());
    },

    close_drawers() {
      this.search_overlay_open = false;
      this.conversation_details_open = false;
      this.active_context_menu = null;
      this.active_message_menu_id = null;
    },

    close_outside_menus(event) {
      if (event.target.closest('.of-context-menu, .of-message-more-menu, .of-quick-actions, .of-message-actions')) return;
      if (event.target.closest('.floating-chat-widget')) {
        this.active_context_menu = null;
        this.active_message_menu_id = null;
        return;
      }
      this.close_drawers();
    },

    start_mobile_drag(event) {
      if (!window.matchMedia('(max-width: 767.98px)').matches) return;
      this.mobile_drag_start_y = event.touches?.[0]?.clientY || 0;
      this.mobile_drag_delta = 0;
    },

    move_mobile_drag(event) {
      if (!this.mobile_drag_start_y) return;
      const current_y = event.touches?.[0]?.clientY || this.mobile_drag_start_y;
      this.mobile_drag_delta = Math.max(0, current_y - this.mobile_drag_start_y);
    },

    end_mobile_drag() {
      if (this.mobile_drag_delta > 90) this.collapse_chat();
      this.mobile_drag_start_y = 0;
      this.mobile_drag_delta = 0;
    },

    handle_keydown(event) {
      if (event.key === 'Escape') this.close_drawers();
    },

    show_toast(title, text, tone = 'success') {
      this.toast = { title, text, tone };
      window.clearTimeout(this.toast_timer);
      this.toast_timer = window.setTimeout(() => {
        this.toast = null;
      }, 3000);
    },

    scroll_to_bottom(force = false) {
      this.$nextTick(() => {
        const scroller = this.$refs.message_scroller;
        if (!scroller) return;
        if (force || !this.user_scrolled_up) scroller.scrollTop = scroller.scrollHeight;
      });
    },

    set_scroll_top(el, value) {
      this._is_adjusting_scroll = true;
      el.scrollTop = value;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._is_adjusting_scroll = false;
        });
      });
    },

    on_message_scroll() {
      if (this._is_adjusting_scroll) return;
      const scroller = this.$refs.message_scroller;
      if (!scroller) return;
      const distance = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
      this.user_scrolled_up = distance > 120;
      if (!this.user_scrolled_up) this.new_messages_banner = false;
      if (scroller.scrollTop < 80 && this.messages_has_more && !this.loading_more_messages) this.load_more_messages();
    },

    jump_to_bottom() {
      this.user_scrolled_up = false;
      this.new_messages_banner = false;
      this.scroll_to_bottom(true);
    },

    ...floating_chat_action_methods,
    ...floating_chat_send_methods,
    ...floating_chat_display_methods,
  },
};
